"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LinkCertificateState = exports.LinkCertificateServingRequest = exports.LinkCertificateResponse = exports.LinkCertificateSummary = exports.LinkCertificateRole = exports.LinkCertificateRequest = exports.LINK_CERTIFICATE_MAX_ADDRESSES = exports.LINK_CERTIFICATE_SWITCH_DEADLINE_DAYS = exports.LINK_CERTIFICATE_RENEW_AT_FRACTION = exports.LINK_CERTIFICATE_VALIDITY_DAYS = exports.CertificateFingerprint = exports.CERTIFICATE_FINGERPRINT_PATTERN = void 0;
exports.canonicalLinkAddress = canonicalLinkAddress;
exports.advertisedLinkHost = advertisedLinkHost;
const zod_1 = require("zod");
/**
 * Link LAN TLS (RL-457, ADR-0014 §3): how a Link gets the certificate it
 * serves its tills with, and how the tills learn which certificate to trust.
 *
 * ------------------------------------------------------------------
 * THE TRUST MODEL, IN ONE PARAGRAPH
 * ------------------------------------------------------------------
 * A till trusts its Link by FINGERPRINT, not by chain: it accepts a TLS
 * connection to `link.url` if and only if the SHA-256 of the DER leaf
 * certificate the server presents is one of `link.certificateFingerprints`
 * from `sync.store-link`. It uses no CA store, checks no hostname and no
 * validity dates — the Link switches to its next certificate before the old
 * one expires, and a pin is already stronger than any of those checks. Back
 * Office still signs each certificate with a per-tenant CA so a certificate
 * carries its provenance, but a till never needs that CA.
 *
 * ------------------------------------------------------------------
 * ROLLOVER
 * ------------------------------------------------------------------
 * Validity is one year. The Link renews at two thirds of it with a NEW key.
 * From then on Back Office publishes both fingerprints (current first), and
 * records which tills of the store have received the new one. The Link keeps
 * serving the old certificate until every till that had the old fingerprint
 * has received the new one (`readyToSwitch`), or until 30 days before the old
 * one expires, whichever comes first. It then swaps the certificate on its
 * listener — connections already open are not dropped — and reports the new
 * fingerprint; Back Office stops publishing the old one.
 */
/** `sha256:` + 64 lowercase hex digits: SHA-256 of the DER leaf certificate. */
exports.CERTIFICATE_FINGERPRINT_PATTERN = /^sha256:[0-9a-f]{64}$/;
exports.CertificateFingerprint = zod_1.z.string().regex(exports.CERTIFICATE_FINGERPRINT_PATTERN);
/** Every LAN certificate is issued for this long. */
exports.LINK_CERTIFICATE_VALIDITY_DAYS = 365;
/** The Link asks for its next certificate once this share of the lifetime has passed. */
exports.LINK_CERTIFICATE_RENEW_AT_FRACTION = 2 / 3;
/**
 * The Link switches to its next certificate no later than this many days
 * before the one it serves expires, even if some till has not received the
 * new fingerprint yet: a till that has not synced for that long has to pull
 * `sync.store-link` again before it reaches the Link, and an expired
 * certificate would be worse for everyone.
 */
exports.LINK_CERTIFICATE_SWITCH_DEADLINE_DAYS = 30;
/** Most LAN addresses one certificate may name. */
exports.LINK_CERTIFICATE_MAX_ADDRESSES = 8;
/**
 * One address the Link answers on: an IPv4 address in dotted decimal, an IPv6
 * address, or a DNS name (RFC 1123, no wildcard, no trailing dot). Returns the
 * canonical form (lower case, IPv6 compressed) or null when it is not one.
 *
 * Written without `node:net` so the Link, Back Office and the admin UI all run
 * the same rule. The rule is deliberately narrow: a SAN is what a TLS client
 * may match, so anything ambiguous — `010.0.0.1`, `*.store`, `0.0.0.0` — is
 * refused rather than interpreted.
 */
function canonicalLinkAddress(input) {
    const value = input.trim().toLowerCase();
    if (value === '' || value.length > 253)
        return null;
    const v4 = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;
    if (v4.test(value)) {
        // "This host" and broadcast are never an address a till can reach.
        if (value === '0.0.0.0' || value === '255.255.255.255')
            return null;
        const first = Number(value.split('.')[0]);
        if (first >= 224)
            return null; // multicast / reserved
        return value;
    }
    if (value.includes(':')) {
        const bare = value.replace(/^\[|\]$/g, '');
        if (!/^[0-9a-f:.]+$/.test(bare))
            return null; // no zone ids
        let hostname;
        try {
            hostname = new URL(`https://[${bare}]/`).hostname;
        }
        catch {
            return null;
        }
        const canonical = hostname.replace(/^\[|\]$/g, '');
        if (canonical === '::' || canonical.startsWith('ff'))
            return null; // unspecified / multicast
        return canonical;
    }
    const label = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;
    const labels = value.split('.');
    if (!labels.every((part) => label.test(part)))
        return null;
    // All-numeric last label reads as a malformed IP to half the TLS stacks.
    if (/^\d+$/.test(labels[labels.length - 1]))
        return null;
    return value;
}
/** The host of an advertised URL, canonical, or null when the URL is not acceptable. */
function advertisedLinkHost(url) {
    let parsed;
    try {
        parsed = new URL(url);
    }
    catch {
        return null;
    }
    if (parsed.protocol !== 'https:')
        return null;
    if (parsed.username !== '' || parsed.password !== '')
        return null;
    if (parsed.pathname !== '/' || parsed.search !== '' || parsed.hash !== '')
        return null;
    return canonicalLinkAddress(parsed.hostname);
}
/**
 * `POST /api/v1/link/certificate` — Link token only.
 *
 * The Link generates the key pair; the private key never leaves it. The CSR
 * proves possession and carries the public key; everything else in the
 * certificate is decided by Back Office from the validated declaration below,
 * never copied from the CSR.
 */
exports.LinkCertificateRequest = zod_1.z
    .object({
    /** PKCS#10, PEM. ECDSA P-256 key, signed with SHA-256. */
    csr: zod_1.z.string().min(1).max(16_384),
    /**
     * The URL tills are told to use, `https://<address>:<port>` with no path.
     * Its host must be one of `addresses`.
     */
    advertisedUrl: zod_1.z.string().min(1).max(300),
    /**
     * Every address the certificate covers (the Link's `LINK_LAN_ADDRESSES`
     * plus its advertised host). The CSR's subjectAltName must name exactly
     * these — anything else is refused.
     */
    addresses: zod_1.z.array(zod_1.z.string().min(1).max(253)).min(1).max(exports.LINK_CERTIFICATE_MAX_ADDRESSES),
    /**
     * The fingerprint the Link serves right now, if any. When it is Back
     * Office's current certificate this request is a RENEWAL: the new
     * certificate becomes `next` and both are published until the switch.
     * When it is null or unknown the Link has nothing tills can reach it with
     * (first activation, re-activation, lost key, a new advertised URL), so the new
     * certificate becomes `current` at once.
     */
    servingFingerprint: exports.CertificateFingerprint.nullable(),
})
    .strict();
exports.LinkCertificateRole = zod_1.z.enum(['current', 'next']);
exports.LinkCertificateSummary = zod_1.z.object({
    fingerprint: exports.CertificateFingerprint,
    advertisedUrl: zod_1.z.string().url(),
    addresses: zod_1.z.array(zod_1.z.string()),
    notBefore: zod_1.z.string().datetime(),
    notAfter: zod_1.z.string().datetime(),
});
exports.LinkCertificateResponse = exports.LinkCertificateSummary.extend({
    role: exports.LinkCertificateRole,
    /** The leaf, PEM. */
    certificate: zod_1.z.string().min(1),
    /** The issuing tenant CA, PEM — served after the leaf; tills do not need it. */
    caCertificate: zod_1.z.string().min(1),
});
/**
 * `POST /api/v1/link/certificate/serving` — Link token only. The Link says
 * which fingerprint its listener presents; Back Office answers with what it
 * publishes and whether the tills are ready for the next one.
 *
 * A narrow route of its own so this ticket does not pre-empt the Link status
 * report (RL-453), which carries the same fingerprint among everything else;
 * the two can be folded together there.
 */
exports.LinkCertificateServingRequest = zod_1.z
    .object({ fingerprint: exports.CertificateFingerprint })
    .strict();
exports.LinkCertificateState = zod_1.z.object({
    /** What Back Office publishes first. Null when it has none for this Link and store. */
    current: exports.LinkCertificateSummary.nullable(),
    next: exports.LinkCertificateSummary.extend({
        issuedAt: zod_1.z.string().datetime(),
        /**
         * Tills of the store that had the current fingerprint and have not yet
         * received this one. The switch waits for zero (or for the deadline).
         */
        tillsPending: zod_1.z.number().int().min(0),
        readyToSwitch: zod_1.z.boolean(),
    }).nullable(),
});
//# sourceMappingURL=link-certificate.js.map